import React, { useState } from 'react';
import { useSite } from '../../context/SiteContext';
import { SubscriptionPlanId } from '../../types';
import { Copy, Sparkles, CheckCircle2, X, Building, Globe, Layers } from 'lucide-react';

interface Props {
  isOpen: boolean;
  onClose: () => void;
}

export const SiteDuplicateModal: React.FC<Props> = ({ isOpen, onClose }) => {
  const { activeSite, createNewSite, updateSiteData } = useSite();

  const [newSiteName, setNewSiteName] = useState(activeSite?.name ? `${activeSite.name} — Fethiye Şubesi` : '');
  const [newSubdomain, setNewSubdomain] = useState(activeSite?.subdomain ? `${activeSite.subdomain}-fethiye` : '');
  const [newCity, setNewCity] = useState('Muğla');
  const [newDistrict, setNewDistrict] = useState('Fethiye');
  const [isDuplicating, setIsDuplicating] = useState(false);
  const [success, setSuccess] = useState(false);

  if (!isOpen || !activeSite) return null;

  const handleDuplicate = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsDuplicating(true);

    try {
      // Clone activeSite pages, sections, and settings with updated branch info
      const clonedPages = JSON.parse(JSON.stringify(activeSite.pages));
      const clonedSettings = JSON.parse(JSON.stringify(activeSite.settings || {}));

      clonedSettings.businessName = newSiteName;
      clonedSettings.city = newCity;
      clonedSettings.district = newDistrict;
      clonedSettings.siteName = newSiteName;

      const newSite = await createNewSite(
        newSiteName,
        newSubdomain.toLowerCase().replace(/[^a-z0-9-]/g, ''),
        activeSite.industry,
        activeSite.templateId || 'corporate-standard',
        (activeSite.subscription?.planId as SubscriptionPlanId) || 'PLAN_CORPORATE'
      );

      if (newSite) {
        await updateSiteData({
          pages: clonedPages,
          settings: clonedSettings,
        });
      }

      setSuccess(true);
      setTimeout(() => {
        setSuccess(false);
        onClose();
      }, 1500);
    } catch (err) {
      console.error('Site duplicate error:', err);
    } finally {
      setIsDuplicating(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 bg-slate-950/75 backdrop-blur-xs flex items-center justify-center p-4">
      <div className="bg-white rounded-2xl max-w-md w-full p-6 space-y-5 shadow-2xl border border-slate-200">
        <div className="flex items-center justify-between pb-3 border-b border-slate-100">
          <div className="flex items-center gap-2 text-indigo-600">
            <Copy className="w-5 h-5" />
            <h2 className="text-base font-bold text-slate-900">Site Kopyalama / Şube Çoğaltma</h2>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="p-1 rounded-lg hover:bg-slate-100 text-slate-400"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {success ? (
          <div className="p-4 rounded-xl bg-emerald-50 border border-emerald-200 text-emerald-800 text-xs font-semibold flex items-center gap-2">
            <CheckCircle2 className="w-5 h-5 text-emerald-600 shrink-0" />
            <span>Site başarıyla kopyalandı ve yeni şube sitesi oluşturuldu!</span>
          </div>
        ) : (
          <form onSubmit={handleDuplicate} className="space-y-4">
            <p className="text-xs text-slate-500 leading-relaxed">
              Mevcut sitenizin tüm tasarımını, sayfalarını ve hizmetlerini başka bir lokasyon veya yeni bir şube için anında klonlayın.
            </p>

            <div className="space-y-1">
              <label className="text-xs font-bold text-slate-700">Yeni Site / Şube Adı *</label>
              <input
                type="text"
                value={newSiteName}
                onChange={(e) => setNewSiteName(e.target.value)}
                className="w-full px-3 py-2 rounded-xl bg-slate-50 border border-slate-300 text-xs font-semibold text-slate-900 focus:bg-white focus:border-indigo-500 outline-hidden"
                required
              />
            </div>

            <div className="space-y-1">
              <label className="text-xs font-bold text-slate-700">Web Adresi (URL Slug) *</label>
              <div className="flex items-center gap-1.5 px-3 py-2 rounded-xl bg-slate-50 border border-slate-300 focus-within:bg-white focus-within:border-indigo-500">
                <span className="text-xs font-mono text-slate-500 select-none">adiosoft.com/</span>
                <input
                  type="text"
                  value={newSubdomain}
                  onChange={(e) => setNewSubdomain(e.target.value)}
                  className="flex-1 bg-transparent text-xs font-mono text-slate-900 outline-hidden"
                  required
                />
              </div>
            </div>

            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1">
                <label className="text-xs font-bold text-slate-700">Yeni İl</label>
                <input
                  type="text"
                  value={newCity}
                  onChange={(e) => setNewCity(e.target.value)}
                  className="w-full px-3 py-2 rounded-xl bg-slate-50 border border-slate-300 text-xs text-slate-900 focus:bg-white focus:border-indigo-500 outline-hidden"
                />
              </div>

              <div className="space-y-1">
                <label className="text-xs font-bold text-slate-700">Yeni İlçe</label>
                <input
                  type="text"
                  value={newDistrict}
                  onChange={(e) => setNewDistrict(e.target.value)}
                  className="w-full px-3 py-2 rounded-xl bg-slate-50 border border-slate-300 text-xs text-slate-900 focus:bg-white focus:border-indigo-500 outline-hidden"
                />
              </div>
            </div>

            <div className="pt-3 border-t border-slate-100 flex items-center justify-end gap-3">
              <button
                type="button"
                onClick={onClose}
                className="px-4 py-2 rounded-xl bg-slate-100 hover:bg-slate-200 text-slate-700 text-xs font-bold"
              >
                İptal
              </button>
              <button
                type="submit"
                disabled={isDuplicating}
                className="px-5 py-2 rounded-xl bg-indigo-600 hover:bg-indigo-500 text-white text-xs font-bold shadow-md disabled:opacity-50 flex items-center gap-1.5"
              >
                <Copy className="w-3.5 h-3.5" />
                <span>{isDuplicating ? 'Kopyalanıyor...' : 'Siteyi Klonla'}</span>
              </button>
            </div>
          </form>
        )}
      </div>
    </div>
  );
};
